/**
 * swipePager — horizontal swipe-to-page for small paginated widgets (the
 * tutorial task board, the daily challenges card).
 *
 * Mirrors UIManager.setupTouchSwipe's axis lock (first decisive movement
 * wins; ties lock vertical so a list scroll is never hijacked), at widget
 * scale. Callers must ALSO set `data-no-swipe` on the widget root so the
 * same gesture doesn't leak into the screen-level pager — this helper only
 * decides the widget's own paging, it does not (and must not) stop
 * propagation, so native vertical scrolling over the widget keeps working.
 */
export function attachSwipePager(el: HTMLElement, onSwipe: (dir: 1 | -1) => void): void {
  let startX = 0;
  let startY = 0;
  let axis: 'h' | 'v' | null = null;
  const AXIS_DEADZONE = 12;
  const SWIPE_THRESHOLD = 40; // widget-scale (screen pager uses 80)

  el.addEventListener('touchstart', (e) => {
    const t = e.changedTouches[0];
    startX = t.screenX;
    startY = t.screenY;
    axis = null;
  }, { passive: true });

  el.addEventListener('touchmove', (e) => {
    if (axis !== null) return;
    const t = e.changedTouches[0];
    const dx = Math.abs(t.screenX - startX);
    const dy = Math.abs(t.screenY - startY);
    if (Math.max(dx, dy) < AXIS_DEADZONE) return;
    axis = dx > dy ? 'h' : 'v';
  }, { passive: true });

  el.addEventListener('touchend', (e) => {
    if (axis !== 'h') return;
    const t = e.changedTouches[0];
    const diff = startX - t.screenX;
    const dy = startY - t.screenY;
    if (Math.abs(diff) < SWIPE_THRESHOLD) return;
    // Horizontal travel must clearly dominate (same backstop as UIManager).
    if (Math.abs(diff) <= Math.abs(dy) * 1.5) return;
    onSwipe(diff > 0 ? 1 : -1); // swipe left = next page
  }, { passive: true });
}
